import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useClasses from "hooks/useClasses";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Grid,
} from "@mui/material";

// Icons
import StoreIcon from "@mui/icons-material/Store";
import TimelineIcon from "@mui/icons-material/Timeline";
import RecyclingIcon from "@mui/icons-material/Recycling";
import SpeedIcon from "@mui/icons-material/Speed";
import MapIcon from "@mui/icons-material/Map";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Actions & Components
import {
  getMapCollectionNetwork,
  getMapRecyclingPerformance,
} from "store/actions/analytics";
import MetricCard from "views/Admin/Analytics/MetricCard";
import AnalyticsFilters from "views/Admin/Analytics/components/AnalyticsFilters";
import CohortCard from "./CohortCard";
import styles from "./companyAnalyticsStyle";

export default function CompanyAnalytics() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    collectionNetwork,
    recyclingPerformance,
    analyticsFilters = { period: "7d" },
    analyticsLoading,
  } = useSelector((state) => state.analytics);

  const fetchAll = useCallback(
    (filters) => {
      dispatch(getMapCollectionNetwork(filters, history));
      dispatch(getMapRecyclingPerformance(filters, history));
    },
    [dispatch, history]
  );

  useEffect(() => {
    if (analyticsFilters.period !== "custom") {
      fetchAll(analyticsFilters);
    } else if (analyticsFilters.from && analyticsFilters.to) {
      fetchAll(analyticsFilters);
    }
  }, [analyticsFilters, fetchAll]);

  const handleRefresh = () => {
    fetchAll(analyticsFilters);
  };

  // Safe extraction of network data
  const netData = collectionNetwork?.data || collectionNetwork || {};
  const netMetrics = netData.metrics || {};
  const statusDist = netData.status_distribution || {};
  const activationCohortsObj = netData.activation_cohorts || {};

  // Safe extraction of recycling performance data
  const perfData = recyclingPerformance?.data || recyclingPerformance || {};
  const perfMetrics = perfData.metrics || {};
  const meta = perfData.meta || netData.meta || {};

  const isLoading = analyticsLoading && !collectionNetwork && !recyclingPerformance;

  if (isLoading) {
    return (
      <div className={classes.loadingOverlay}>
        <CircularProgress size={32} sx={{ color: "#6366f1", mr: 1.5 }} />
        Loading company analytics...
      </div>
    );
  }

  // Build cohort objects from the keyed activation_cohorts (day_30, day_60, day_90)
  const buildCohort = (key, days) => {
    const raw = activationCohortsObj[key];
    if (raw && typeof raw === "object") return { ...raw, window_days: raw.window_days ?? days };
    return {
      window_days: days,
      eligible_branches: 0,
      activated_branches: 0,
      activation_rate: raw != null ? raw : null,
      available: raw != null,
      reason: raw == null ? `No branches registered within the ${days}-day cohort window` : undefined,
    };
  };
  const cohort30 = buildCohort("day_30", 30);
  const cohort60 = buildCohort("day_60", 60);
  const cohort90 = buildCohort("day_90", 90);

  return (
    <div className={classes.pageWrapper}>
      {/* ── Meta Banner ── */}
      {meta.generated_at && (
        <div className={classes.metaBanner}>
          <div className={classes.metaInfo}>
            <span>Generated: {new Date(meta.generated_at).toLocaleString()}</span>
            {meta.data_through && (
              <span>Data Through: {new Date(meta.data_through).toLocaleDateString()}</span>
            )}
          </div>
          {meta.is_partial && (
            <div className={classes.metaWarning}>
              <WarningAmberIcon style={{ fontSize: 16 }} />
              Partial Data
            </div>
          )}
        </div>
      )}

      {/* ── Control Bar ── */}
      <AnalyticsFilters classes={classes} onRefresh={handleRefresh} showSync={false} />

      {/* ── 1. Branch Health & Network ── */}
      <Box mb={4}>
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              <StoreIcon style={{ color: "#6366f1" }} />
              1. Branch Health & Network
            </Typography>
            <Typography variant="caption" className={classes.sectionSubtitle}>
              Collection network operational status and registered branch distribution
            </Typography>
          </div>
        </div>

        <div className={classes.kpiGrid}>
          <MetricCard
            name="Registered Branches"
            value={netMetrics.registered_branches}
            unit="branches"
            source="Company Network"
            tooltip="Total drop-off locations registered under your company"
          />
          <MetricCard
            name="Active Branches"
            value={netMetrics.active_branches_28_days}
            unit="branches"
            source="Last 28 Days"
            tooltip="Branches with at least one collection in the last 28 days"
          />
          <MetricCard
            name="Partially Active Branches"
            value={statusDist.partially_active != null ? statusDist.partially_active : "N/A"}
            unit="branches"
            source="29–90 Days"
            tooltip="No collection in 28d, but active within 90d"
          />
          <MetricCard
            name="Inactive Branches"
            value={statusDist.inactive != null ? statusDist.inactive : "N/A"}
            unit="branches"
            source="90+ Days"
            tooltip="No collection in 90+ days or closed"
          />
          <MetricCard
            name="Avg Items Per Registered Branch"
            value={netMetrics.average_collected_items_per_registered_branch}
            unit="items/branch"
            source="Derived"
            tooltip="Total collected items / registered branches"
          />
          <MetricCard
            name="Total Network Collected Items"
            value={perfMetrics.collected_items}
            unit="items"
            source="Recycling Performance"
            tooltip="Total items collected across all registered branches"
          />
        </div>
      </Box>

      {/* ── 2. New-Branch Activation Cohorts ── */}
      <Box mb={4}>
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              <TimelineIcon style={{ color: "#3b82f6" }} />
              2. New-Branch Activation Cohorts
            </Typography>
            <Typography variant="caption" className={classes.sectionSubtitle}>
              Percentage of new branches recording their first collection within 30, 60, and 90 days
            </Typography>
          </div>
        </div>

        <div className={classes.cohortGrid}>
          <CohortCard windowDays={30} cohort={cohort30} />
          <CohortCard windowDays={60} cohort={cohort60} />
          <CohortCard windowDays={90} cohort={cohort90} />
        </div>
      </Box>

      {/* ── 3. Collection Performance ── */}
      <Box mb={4}>
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              <RecyclingIcon style={{ color: "#10b981" }} />
              3. Collection Performance
            </Typography>
            <Typography variant="caption" className={classes.sectionSubtitle}>
              Total requests, drop-offs, user participation, and rewards issued
            </Typography>
          </div>
        </div>

        <div className={classes.kpiGrid}>
          <MetricCard
            name="Recycling Requests"
            value={perfMetrics.recycling_requests}
            unit="requests"
            source="OneCircle"
            tooltip="Total recycle requests submitted for your branches"
          />
          <MetricCard
            name="Completed Drop-offs"
            value={perfMetrics.completed_dropoffs}
            unit="drop-offs"
            source="OneCircle"
            tooltip="Successfully collected and approved drop-offs"
          />
          <MetricCard
            name="Distinct Recycling Users"
            value={perfMetrics.recycling_users}
            unit="users"
            source="OneCircle"
            tooltip="Unique users who created at least one request"
          />
          <MetricCard
            name="Completed Drop-off Users"
            value={perfMetrics.completed_dropoff_users}
            unit="users"
            source="OneCircle"
            tooltip="Unique users who completed at least one drop-off"
          />
          <MetricCard
            name="Collected Items"
            value={perfMetrics.collected_items}
            unit="items"
            source="OneCircle"
            tooltip="Total recyclables collected"
          />
          <MetricCard
            name="Reward Points Issued"
            value={perfMetrics.reward_points_issued ?? perfMetrics.points_issued}
            unit="points"
            source="OneCircle"
          />
          <MetricCard
            name="Vouchers Issued"
            value={perfMetrics.vouchers_issued}
            unit="vouchers"
            source="OneCircle"
          />
          <MetricCard
            name="Vouchers Redeemed"
            value={perfMetrics.vouchers_redeemed}
            unit="vouchers"
            source="OneCircle"
          />
        </div>
      </Box>

      {/* ── 4. Operational Efficiency & Derived Ratios ── */}
      <Box mb={4}>
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              <SpeedIcon style={{ color: "#f59e0b" }} />
              4. Operational Efficiency & Ratios
            </Typography>
            <Typography variant="caption" className={classes.sectionSubtitle}>
              Conversion ratios, user intensity, and collection density metrics
            </Typography>
          </div>
        </div>

        <div className={classes.kpiGrid}>
          <MetricCard
            name="Request Completion Rate"
            value={perfMetrics.request_completion_rate}
            unit="%"
            source="Derived"
            tooltip="Completed drop-offs / recycling requests"
          />
          <MetricCard
            name="Recycler Completion Rate"
            value={perfMetrics.recycler_completion_rate}
            unit="%"
            source="Derived"
            tooltip="Completed drop-off users / recycling users"
          />
          <MetricCard
            name="Requests Per Recycler"
            value={perfMetrics.requests_per_recycler}
            unit="req/user"
            source="Derived"
            tooltip="Recycling requests / recycling users"
          />
          <MetricCard
            name="Items Per Completed Drop-off"
            value={perfMetrics.items_per_completed_dropoff}
            unit="items/drop-off"
            source="Derived"
            tooltip="Collected items / completed drop-offs"
          />
          <MetricCard
            name="Points Per Completed Drop-off"
            value={perfMetrics.points_per_completed_dropoff}
            unit="pts/drop-off"
            source="Derived"
            tooltip="Reward points issued / completed drop-offs"
          />
          <MetricCard
            name="Voucher Redemption Rate"
            value={perfMetrics.voucher_redemption_rate}
            unit="%"
            source="Derived"
            tooltip="Vouchers redeemed / vouchers issued"
          />
        </div>
      </Box>

      {/* ── 5. Interactive Map & Branch Comparison ── */}
      <div className={classes.mapPromoCard}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            style={{
              backgroundColor: "rgba(99,102,241,0.15)",
              borderRadius: "12px",
              padding: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#818cf8",
            }}
          >
            <MapIcon style={{ fontSize: 32 }} />
          </Box>
          <div>
            <Typography
              variant="h6"
              style={{
                fontWeight: 700,
                color: isDark ? "#fff" : "#111827",
                marginBottom: "4px",
              }}
            >
              Interactive Branch Map & Geo-Analytics
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: isDark ? "rgba(255,255,255,0.6)" : "#4b5563",
                maxWidth: "600px",
              }}
            >
              Explore individual branch activity, distance radius filters, top-performing branches,
              and geospatial density across governorates and cities.
            </Typography>
          </div>
        </Box>

        <button
          className={classes.mapPromoBtn}
          onClick={() => history.push("/admin/map")}
        >
          <span>Open Interactive Map</span>
          <ArrowForwardIcon style={{ fontSize: 18 }} />
        </button>
      </div>
    </div>
  );
}
